/**
 * RFQ agentic workflow state machine.
 *
 * Mirror of React `front-react/src/lib/rfq-state-machine.ts`.
 * Copy transitions and helper logic character-for-character.
 */
import type { WorkflowState, Phase } from "./types";

// State-to-Phase mapping
export const STATE_TO_PHASE: Record<WorkflowState, Phase> = {
  // Phase 1: Initiation & Requirements
  INITIATED: 1,
  REQUIREMENTS_DEFINED: 1,
  CRITERIA_DEFINED: 1,
  STAKEHOLDERS_IDENTIFIED: 1,
  // Phase 2: RFQ Draft & Publication
  RFQ_DRAFTED: 2,
  RFQ_APPROVED: 2,
  RFQ_PUBLISHED: 2,
  QNA_OPEN: 2,
  QNA_CLOSED: 2,
  // Phase 3: Quote Intake & Negotiation
  QUOTES_UNDER_REVIEW: 3,
  NEGOTIATION_IN_PROGRESS: 3,
  QUOTES_FINALIZED: 3,
  // Phase 4: Evaluation & Award
  EVALUATION_IN_PROGRESS: 4,
  CONSENSUS_CALCULATED: 4,
  AWARD_DECISION_PENDING: 4,
  SUPPLIERS_NOTIFIED: 4,
  CLOSED: 4,
  EVALUATION_REOPENED: 4,
  // Special
  CANCELLED: 1,
  ARCHIVED: 4,
};

// Valid state transitions
const VALID_TRANSITIONS: Record<WorkflowState, WorkflowState[]> = {
  INITIATED: ["REQUIREMENTS_DEFINED", "CANCELLED"],
  REQUIREMENTS_DEFINED: ["CRITERIA_DEFINED", "CANCELLED"],
  CRITERIA_DEFINED: ["STAKEHOLDERS_IDENTIFIED", "CANCELLED"],
  STAKEHOLDERS_IDENTIFIED: ["RFQ_DRAFTED", "CANCELLED"],
  RFQ_DRAFTED: ["RFQ_APPROVED", "CANCELLED"],
  RFQ_APPROVED: ["RFQ_PUBLISHED", "CANCELLED"],
  RFQ_PUBLISHED: ["QNA_OPEN", "CANCELLED"],
  QNA_OPEN: ["QNA_CLOSED", "CANCELLED"],
  QNA_CLOSED: ["QNA_OPEN", "QUOTES_UNDER_REVIEW", "CANCELLED"],
  QUOTES_UNDER_REVIEW: ["NEGOTIATION_IN_PROGRESS", "EVALUATION_IN_PROGRESS", "CANCELLED"],
  NEGOTIATION_IN_PROGRESS: ["QUOTES_UNDER_REVIEW", "QUOTES_FINALIZED", "CANCELLED"],
  QUOTES_FINALIZED: ["EVALUATION_IN_PROGRESS", "CANCELLED"],
  EVALUATION_IN_PROGRESS: ["CONSENSUS_CALCULATED", "CANCELLED"],
  CONSENSUS_CALCULATED: ["AWARD_DECISION_PENDING", "EVALUATION_REOPENED", "CANCELLED"],
  EVALUATION_REOPENED: ["EVALUATION_IN_PROGRESS", "CANCELLED"],
  AWARD_DECISION_PENDING: ["SUPPLIERS_NOTIFIED", "EVALUATION_REOPENED", "CANCELLED"],
  SUPPLIERS_NOTIFIED: ["CLOSED"],
  CLOSED: ["ARCHIVED"],
  CANCELLED: [],
  ARCHIVED: [],
};

export function canTransitionTo(
  currentState: WorkflowState,
  newState: WorkflowState
): boolean {
  const validNextStates = VALID_TRANSITIONS[currentState];
  return validNextStates.includes(newState);
}

export function getPhaseFromState(state: WorkflowState): Phase {
  return STATE_TO_PHASE[state];
}

export function getNextState(currentState: WorkflowState): WorkflowState | null {
  const validNextStates = VALID_TRANSITIONS[currentState];
  // Return first non-special state (not CANCELLED, EVALUATION_REOPENED)
  return validNextStates.find(
    (s) => s !== "CANCELLED" && s !== "EVALUATION_REOPENED"
  ) || null;
}

export function canStartEvaluation(
  state: WorkflowState,
  allQuotesFinalized: boolean
): boolean {
  return (
    (state === "QUOTES_UNDER_REVIEW" || state === "QUOTES_FINALIZED") &&
    allQuotesFinalized
  );
}

export function canStartNegotiation(state: WorkflowState): boolean {
  return state === "QUOTES_UNDER_REVIEW";
}

export function canEditRequirements(state: WorkflowState): boolean {
  const editableStates: WorkflowState[] = [
    "INITIATED",
    "REQUIREMENTS_DEFINED",
    "CRITERIA_DEFINED",
    "STAKEHOLDERS_IDENTIFIED",
  ];
  return editableStates.includes(state);
}

export function canEditCriteria(state: WorkflowState): boolean {
  const editableStates: WorkflowState[] = [
    "INITIATED",
    "REQUIREMENTS_DEFINED",
    "CRITERIA_DEFINED",
    "STAKEHOLDERS_IDENTIFIED",
  ];
  return editableStates.includes(state);
}

export function canEditStakeholders(state: WorkflowState): boolean {
  const editableStates: WorkflowState[] = [
    "INITIATED",
    "REQUIREMENTS_DEFINED",
    "CRITERIA_DEFINED",
    "STAKEHOLDERS_IDENTIFIED",
    "RFQ_DRAFTED",
  ];
  return editableStates.includes(state);
}

export function canEditRFQDocument(state: WorkflowState): boolean {
  const editableStates: WorkflowState[] = [
    "RFQ_DRAFTED",
  ];
  return editableStates.includes(state);
}

export function isPhase1Locked(state: WorkflowState): boolean {
  return ![
    "INITIATED",
    "REQUIREMENTS_DEFINED",
    "CRITERIA_DEFINED",
    "STAKEHOLDERS_IDENTIFIED",
  ].includes(state);
}

export function isPhase2Locked(state: WorkflowState): boolean {
  const phase2States: WorkflowState[] = [
    "RFQ_DRAFTED",
    "RFQ_APPROVED",
    "RFQ_PUBLISHED",
    "QNA_OPEN",
    "QNA_CLOSED",
  ];
  return !phase2States.includes(state);
}

export function isPhase3Locked(state: WorkflowState): boolean {
  const phase3States: WorkflowState[] = [
    "QUOTES_UNDER_REVIEW",
    "NEGOTIATION_IN_PROGRESS",
    "QUOTES_FINALIZED",
  ];
  return !phase3States.includes(state);
}

export function getStateLabel(state: WorkflowState): string {
  const labels: Record<WorkflowState, string> = {
    INITIATED: "Initiated",
    REQUIREMENTS_DEFINED: "Requirements Defined",
    CRITERIA_DEFINED: "Criteria Defined",
    STAKEHOLDERS_IDENTIFIED: "Stakeholders Identified",
    RFQ_DRAFTED: "RFQ Drafted",
    RFQ_APPROVED: "RFQ Approved",
    RFQ_PUBLISHED: "RFQ Published",
    QNA_OPEN: "Q&A Open",
    QNA_CLOSED: "Q&A Closed",
    QUOTES_UNDER_REVIEW: "Quotes Under Review",
    NEGOTIATION_IN_PROGRESS: "Negotiation In Progress",
    QUOTES_FINALIZED: "Quotes Finalized",
    EVALUATION_IN_PROGRESS: "Evaluation In Progress",
    CONSENSUS_CALCULATED: "Consensus Calculated",
    AWARD_DECISION_PENDING: "Award Decision Pending",
    SUPPLIERS_NOTIFIED: "Suppliers Notified",
    CLOSED: "Closed",
    CANCELLED: "Cancelled",
    EVALUATION_REOPENED: "Evaluation Reopened",
    ARCHIVED: "Archived",
  };
  return labels[state];
}

// Step number to state mapping
export function getStateForStep(stepNumber: number): WorkflowState {
  const stepToState: Record<number, WorkflowState> = {
    1: "INITIATED",
    2: "REQUIREMENTS_DEFINED",
    3: "CRITERIA_DEFINED",
    4: "STAKEHOLDERS_IDENTIFIED",
    5: "RFQ_DRAFTED",
    6: "RFQ_APPROVED",
    7: "RFQ_PUBLISHED",
    8: "QNA_CLOSED",
    9: "QUOTES_UNDER_REVIEW",
    10: "NEGOTIATION_IN_PROGRESS",
    11: "QUOTES_FINALIZED",
    12: "EVALUATION_IN_PROGRESS",
    13: "CONSENSUS_CALCULATED",
    14: "AWARD_DECISION_PENDING",
    15: "SUPPLIERS_NOTIFIED",
  };
  return stepToState[stepNumber] || "INITIATED";
}

// State to step number mapping
export function getStepForState(state: WorkflowState): number {
  const stateToStep: Record<WorkflowState, number> = {
    INITIATED: 1,
    REQUIREMENTS_DEFINED: 2,
    CRITERIA_DEFINED: 3,
    STAKEHOLDERS_IDENTIFIED: 4,
    RFQ_DRAFTED: 5,
    RFQ_APPROVED: 6,
    RFQ_PUBLISHED: 7,
    QNA_OPEN: 8,
    QNA_CLOSED: 8,
    QUOTES_UNDER_REVIEW: 9,
    NEGOTIATION_IN_PROGRESS: 10,
    QUOTES_FINALIZED: 11,
    EVALUATION_IN_PROGRESS: 12,
    CONSENSUS_CALCULATED: 13,
    EVALUATION_REOPENED: 12,
    AWARD_DECISION_PENDING: 14,
    SUPPLIERS_NOTIFIED: 15,
    CLOSED: 15,
    CANCELLED: 1,
    ARCHIVED: 15,
  };
  return stateToStep[state] || 1;
}
